'use client';

import { cn } from '@ahm/common-helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import BannerSection from '../../(main)/store/components/store-banner-section';
import StoreCard from './store-card';
import StoreList from './store-list-client';
import type { Store } from '@/api/ahafood';
import type { FoodOrderCategory } from '@/api/ahafood/get-category-fn';
import type { Tag } from '@/api/ahafood/get-deal-fn';
import { getStoresFn } from '@/api/ahafood/get-store-fn';
import type { Progress } from '@/api/use-get-deal-progress';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore } from '@/store/use-ai-store';

export type Payload = {
  city_id: string;
  category_slug?: string;
  promotion?: boolean;
  freeship?: 'freeship' | 'freeship-100' | false;
  special_request?: string;
  hot_deal?: boolean;
  campaign_id?: string;
  text?: string;
  lat?: number;
  lng?: number;
  radius?: number;
  open_now?: boolean;
};

export type StoreListingProps = {
  data: Store[];
  deal?: Tag[];
  deal_progress?: Progress[];
  categories?: FoodOrderCategory[];
  payload: Payload;
  processedDeals?: Record<string, any>;
};

// Tách logic tính toán banner ra ngoài component
const getBannerInterval = (isMobile: boolean, screenWidth?: number) => {
  if (isMobile) return 5;
  if (screenWidth && screenWidth > 1023) return 8;
  return 3;
};

const shouldShowBannerAtIndex = (index: number, interval: number) => {
  return (index + 1) % interval === 0 && (interval !== 8 || index !== 0);
};

const getBannerData = (
  index: number,
  interval: number,
  bannerKeys: string[],
  processedDeals: Record<string, any>
) => {
  const bannerIndex = Math.floor(index / interval);
  const bannerKey = bannerKeys[bannerIndex];
  return bannerKey ? processedDeals[bannerKey] : null;
};

export default function SimpleStoreList({
  data,
  payload,
  processedDeals,
}: StoreListingProps) {
  const isMobile = useIsMobile();
  const address = useAiFoodOrderStore.use.address();
  const lat = address?.lat;
  const lng = address?.lng;
  const limit = 20;

  // Setup infinite query for fetching more stores
  const {
    data: storesData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['stores', payload],
    queryFn: async ({ pageParam = 0 }) => {
      return getStoresFn({
        ...payload,
        limit,
        skip: pageParam,
        ...(lat && lng ? { lat, lng, radius: 20000 } : {}),
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.stores) return undefined;
      const loaded = allPages.reduce(
        (acc, page) => acc + (page?.stores?.length || 0),
        0
      );
      return lastPage.stores.length < limit ? undefined : loaded;
    },
    initialData: data?.length
      ? {
          pages: [{ stores: data, total: data.length }],
          pageParams: [0],
        }
      : undefined,
  });

  // Get all stores from infinite query
  const allStores = useMemo(() => {
    return (
      storesData?.pages?.flatMap((page) => page.stores).filter(Boolean) ?? []
    );
  }, [storesData?.pages]);

  const bannerConfig = useMemo(() => {
    if (!processedDeals) return { keys: [], interval: 0 };

    const keys = Object.keys(processedDeals).filter((key) =>
      key.startsWith('banners')
    );
    const interval = getBannerInterval(isMobile);

    return { keys, interval };
  }, [processedDeals, isMobile]);

  const renderStoreWithBanners = useCallback(() => {
    if (!allStores?.length || !bannerConfig.keys.length) return null;

    const items: React.ReactNode[] = [];

    for (let i = 0; i < allStores.length; i++) {
      const store = allStores[i];
      if (!store?._id) continue;

      items.push(<StoreCard key={store._id} data={store} />);

      if (shouldShowBannerAtIndex(i, bannerConfig.interval)) {
        const bannerData = getBannerData(
          i,
          bannerConfig.interval,
          bannerConfig.keys,
          processedDeals!
        );

        if (bannerData) {
          items.push(
            <div
              key={`banner-${i}`}
              className={cn(
                'col-span-full w-full overflow-hidden',
                isMobile ? 'my-4' : 'md:col-span-4 md:my-6'
              )}
            >
              <BannerSection banners={bannerData} isLoading={false} />
            </div>
          );
        }
      }
    }

    return items;
  }, [allStores, bannerConfig, processedDeals, isMobile]);

  if (!allStores?.length) {
    return <StoreList data={data} payload={payload} />;
  }

  if (processedDeals && bannerConfig.keys.length > 0) {
    return (
      <div className="space-y-6">
        <InfiniteScroll
          className={cn(
            'grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
            'auto-rows-max items-stretch justify-items-stretch'
          )}
          dataLength={allStores.length}
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={<p className="col-span-full text-center">Đang tải thêm...</p>}
          endMessage={<p className="col-span-full text-center"></p>}
        >
          {renderStoreWithBanners()}
        </InfiniteScroll>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InfiniteScroll
        className={cn(
          'grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
          'auto-rows-max items-stretch justify-items-stretch'
        )}
        dataLength={allStores.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<p className="col-span-full text-center">Đang tải thêm...</p>}
        endMessage={<p className="col-span-full text-center"></p>}
      >
        {allStores.map((store) => (
          <StoreCard key={store._id} data={store} />
        ))}
      </InfiniteScroll>
    </div>
  );
}
